/**
 * Whether Mum Hub exists on this website (MAR-219).
 *
 * Mum Hub is finished before it is announced, and merging is deploying here:
 * there is no staging site, so anything on `main` is live to every visitor
 * within a few minutes. The flag is what makes the merge safe.
 *
 * ## This module is only TWO of the three ways in
 *
 * `gate.ts` is what a component asks. It ORs what this module decides with a
 * third arm: a signed-in customer who already has Mum Hub, from
 * `GET /api/mum-hub/entitlements`. That arm is asynchronous and this one is
 * not, which is exactly why they are separate files. Everything below is about
 * the build-time and browser-local halves.
 *
 * ## Two switches, and they are not the same switch
 *
 * 1. THIS ONE decides whether the feature EXISTS: the nav entries, the three
 *    routes, the prerendered landing page and its sitemap entry. Off means a
 *    visitor cannot reach Mum Hub at all and Google is never told about it.
 * 2. `MumHubWebConfig.enabled`, from `GET /api/mum-hub/web-config`, decides
 *    whether the feature may TAKE MONEY. Off means the pages are there and the
 *    purchase UI says it is not open yet.
 *
 * They fail in different directions, which is why they are separate. Turning
 * this one on with no Stripe products configured must show a page that says
 * "not open yet", never a button that answers 400. See `MUM_HUB_WEB_CLOSED`
 * in `api.ts` for the other half.
 *
 * ## Opt-in, not a kill switch
 *
 * `VITE_APP_BANNER_ENABLED` is a kill switch: on unless the value is exactly
 * "false". This is the opposite, and deliberately so. That banner had already
 * shipped and been seen; this feature has not, and an unset variable on a
 * preview deploy, a fresh clone or a local `npm run build` must mean OFF. So
 * only the exact string "true" turns it on, and every other value, the empty
 * string and the variable being absent all leave it off.
 *
 * HOW ADRIAN FLIPS IT: Cloudflare dashboard, Pages, the Capsule Chic Hub
 * project, Settings, Environment variables. Add `VITE_MUM_HUB_WEB_ENABLED`
 * with the value `true` to Production, then re-run the latest deployment. It
 * is a build-time variable, so it needs a deploy and not just a save. Removing
 * it, or setting it to anything else, and redeploying takes the feature away
 * again.
 *
 * ## The runtime override, and what it is for
 *
 * A build-time flag cannot be tried out on production without a deploy that
 * everybody sees. So the browser may also turn the feature on for ITSELF, by
 * running this in the console on capsulechichub.com:
 *
 *     localStorage.setItem('cch:mum-hub-web', '1')
 *
 * and reloading. `localStorage.removeItem('cch:mum-hub-web')` puts it back.
 *
 * This is not a security control and is not pretending to be one. The pages it
 * reveals are React routes in a bundle anybody can read, and every one of them
 * that shows a customer anything reads it from the Worker behind a session and
 * an entitlement check. The override buys a preview of the LAYOUT, and buys
 * nothing at all that the API would not have handed the same person anyway.
 *
 * ## Why it is read once
 *
 * {@link isMumHubWebEnabled} is evaluated when the module loads and the answer
 * is frozen into `MUM_HUB_WEB_ENABLED`, because the router, the header and the
 * footer must all agree within one page view. A value that changed mid-session
 * would give a nav link that leads to the not-found page. A reload is the
 * price of flipping the override, and it is stated above.
 */

/**
 * The localStorage key. `cch:` rather than the `cch.` used by the app banner
 * and the anon id: those are device-local UI state, this is a developer
 * override, and the different separator keeps them visibly different things in
 * a storage inspector.
 */
export const MUM_HUB_OVERRIDE_KEY = "cch:mum-hub-web";

/** The only value that turns the override on. */
export const MUM_HUB_OVERRIDE_VALUE = "1";

/**
 * The build-time half. Exactly "true", nothing else.
 *
 * Kept as its own exported function so the string rule is testable without
 * touching storage, and so `scripts/lib/mum-hub-seo.mjs`, which has to make
 * the same decision in Node from `process.env`, has something to be checked
 * against. `scripts/__tests__/mum-hub-seo.test.mjs` pins the two to the same
 * table of values.
 */
export function isMumHubFlagValue(raw: string | undefined): boolean {
  return raw === "true";
}

/** Has this browser turned the feature on for itself? Best effort. */
export function hasMumHubOverride(): boolean {
  try {
    return window.localStorage.getItem(MUM_HUB_OVERRIDE_KEY) === MUM_HUB_OVERRIDE_VALUE;
  } catch {
    // Private browsing, lockdown mode, storage disabled. Fail CLOSED: an
    // override nobody can read is an override nobody set.
    return false;
  }
}

/**
 * The build flag OR the browser override. Either one is enough.
 *
 * Takes both inputs as arguments so the whole decision is reachable from a
 * test without stubbing `import.meta.env` and `localStorage` at once, which is
 * how the app banner's flag test got its own coverage.
 */
export function isMumHubWebEnabled(
  buildFlag: string | undefined = import.meta.env.VITE_MUM_HUB_WEB_ENABLED as
    | string
    | undefined,
  override: boolean = typeof window !== "undefined" ? hasMumHubOverride() : false,
): boolean {
  return isMumHubFlagValue(buildFlag) || override;
}

/**
 * The answer for this page view. Read once, at module load, so the router, the
 * header and the footer cannot disagree with each other.
 */
export const MUM_HUB_WEB_ENABLED = isMumHubWebEnabled();
